/**
 * The service worker's API runtime-cache namespace and its purge — ONE module shared by the
 * service worker (service-worker.ts purges on `activate`) and the app (the auth store purges on
 * every session-principal change: Switch firm and sign-out).
 *
 * WHY THIS EXISTS (cross-firm exposure): the SPA serves every firm from ONE origin with an in-tab
 * "Switch firm", and case numbers are per-firm-per-year, so `/api/v1/cases/ARG-2026-0001` is the
 * SAME URL in every firm. Cache Storage entries are keyed by URL only (no firm, no cookie), so an
 * API response cached under firm A's session can be matched for firm B's identical request —
 * served by the service worker without the server's firm-bind check ever seeing it.
 *
 * The current service worker caches NO API response (see service-worker.ts). This purge removes
 * what a PREVIOUS service-worker build left behind (its `argus-api-*` StaleWhileRevalidate /
 * NetworkFirst caches) and is the defence in depth should an API cache ever be reintroduced.
 *
 * Typechecks under BOTH the DOM lib (app tsconfig) and the WebWorker lib (tsconfig.sw.json):
 * it only touches `CacheStorage`, which both define. Workbox-free, so it is unit-testable in jsdom.
 */

/** The prefix of every Cache Storage name that holds (or ever held) an API response. */
export const API_RUNTIME_CACHE_PREFIX = 'argus-api-';

/** The slice of `CacheStorage` the purge uses (lets tests pass a fake). */
export type CacheStorageLike = Pick<CacheStorage, 'keys' | 'delete'>;

function ambientCacheStorage(): CacheStorageLike | undefined {
  try {
    return (globalThis as { caches?: CacheStorageLike }).caches;
  } catch {
    return undefined;
  }
}

/**
 * Delete every Cache Storage cache whose name starts with {@link API_RUNTIME_CACHE_PREFIX}, and
 * resolve only once every delete has settled. Resolves to the names it deleted.
 *
 * Degrades safely where Cache Storage is UNAVAILABLE — absent (non-secure context, jsdom) or
 * refused by the context (`SecurityError`, e.g. some private-browsing modes): no service worker
 * can be registered there either, so nothing can have been cached and there is nothing to serve.
 * Any OTHER failure (a delete that rejects) propagates: the caller must not treat stale firm data
 * as gone when it may still be servable.
 */
export async function purgeApiRuntimeCaches(
  storage: CacheStorageLike | undefined = ambientCacheStorage(),
): Promise<string[]> {
  if (!storage) return [];
  let names: string[];
  try {
    names = await storage.keys();
  } catch (err) {
    // By name, not `instanceof Error`: a DOMException is not guaranteed to inherit Error.prototype
    // in every realm (it does not under jsdom).
    if ((err as { name?: unknown } | null)?.name === 'SecurityError') return [];
    throw err;
  }
  const targets = names.filter((name) => name.startsWith(API_RUNTIME_CACHE_PREFIX));
  // `delete` resolving `false` means "already gone" (e.g. another tab purged first) — fine.
  await Promise.all(targets.map((name) => storage.delete(name)));
  return targets;
}
